/**
 * 全量上报点击事件
 *
 * 提供方法
 * registerEvent: 注册事件，目前只有click
 * 参数：
 * el: 主要监听的节点，不传默认为document，可以结合router守卫监听指定的页面
 * useCapture: 事件句柄在捕获、冒泡阶段执行
 */

import { biHottag } from './mta';
import { CustomException } from '../error';

let fullReportTimer; // 上报定时器，用于取消定时器
let fullReportBusiness;
function isNode(el) {
  return el && el.nodeType === Node.ELEMENT_NODE;
}

function isDocument(el) {
  return el && el.nodeType === Node.DOCUMENT_NODE;
}

function isTag(el, tag) {
  return el && el.tagName && (el.tagName.toLowerCase() === tag.toLowerCase());
}

function getTarget(e) {
  if (typeof e.target === 'undefined') {
    return e.srcElement;
  }

  return e.target || {};
}

function getClassName(el) {
  let { className } = el;
  const { tagName, nodeType } = el;

  if (typeof className === 'object' && className.baseVal) {
    className = className.baseVal;
  }

  if (className && typeof className === 'string') {
    // split切割包含多个空格, 解决部分浏览器不支持trim的问题
    try {
      return `.${className.trim().split(/\s+/)
        .join('.')}`; // trim过滤classname为 " vs-toggle" 的情况
    } catch (err) {
      throw new CustomException(err, 'FullReportClassNameFormatFail', 'in base-libs/full-report.js, 全量上报class格式化失败');
    }
  }

  return '';
}

function getTagName(el) {
  return (el.tagName && el.tagName.toLowerCase()) || '';
}

/**
 * 获取点击对象的xPath路径。解释路径组成: 标签div名称 + 标签index序号 + 标签的class名称（如果有多个将以.拼接）
 * 例如： body.0|div.2|div.0.page-repay|div.0.box|div.0.fit-navbar|div.1.fit-navbar-item|span.0
 * @param e 事件对象
 */
function getXpath(e) {
  let path = [];

  if (e.path && e.path.length > 0 && isNode(e.path[0])) {
    path = e.path;
  } else if (e.target) {
    let target = getTarget(e);

    while (target.parentNode && !isTag(target, 'html')) {
      path.push(target);
      target = target.parentNode;
    }
  }

  if (path.length > 0) {
    return path
      .reverse()
      .filter(el => isNode(el) && !isTag(el, 'html'), // 过滤掉非node点与html的节点，e.path会包含html节点，html节点不需要统计，所以可以去除掉
      )
      .map((el) => {
        try {
          // body.0
          if (el && el.parentNode) {
            const tagName = getTagName(el);
            const className = getClassName(el);

            const parentTagName = getTagName(el.parentNode);
            const parentClassName = getClassName(el.parentNode);

            const selector = `${tagName}${className}`;
            // ul.box > li.item
            const selectors = el.parentNode.querySelectorAll(`${parentTagName}${parentClassName} > ${selector}`);

            const index = Array.prototype.indexOf.call(selectors, el);

            return `${tagName}.${index}${className}`;
          }
        } catch (e) {
          new CustomException(e, 'FullReportMapListError', '全量上报处理数据失败');
          return '';
        }
      })
      .join('|');
  }

  return '';
}

/**
 * 精简函数
 * @param arr 数组
 * @param i 下标
 * @param len 最大长度
 */
function short(arr, i, len) {
  let item = arr[i];
  const sub = item.split('.');
  item = sub.slice(0, sub.length > 2 ? 3 : 2);
  arr[i] = item.join('.');
  const str = arr.join('|');

  // 如果循环到最后一个，就从尾部往前去len位
  if (i >= arr.length - 1) {
    return str.slice(-len);
  }
  if (str.length < len) {
    return str;
  }
  return short(arr, i + 1, len);
}

/**
 * 全埋点上报 - 精简 xPath 长度
 * TODO: getShortPath 借鉴于理财通stat.js
 * @param xpath
 * @param len
 */
function getShortPath(xPath, len) {
  if (xPath.length < len) {
    return xPath;
  }
  const arr = xPath.split('|');
  return short(arr, 0, len);
}

/**
 * 点击回调事件
 * @param e 事件对象
 */
function clickEvent(e) {
  // 获取xPath
  const xPath = getXpath(e);
  const len = xPath.length;

  // 存在xPath，上报bi
  if (len > 0) {
    // TODO: 延迟
    fullReportTimer = setTimeout(() => {
      biHottag(`hkwallet.fullreport.lose.stat`, {
        // getShortPath 借鉴于理财通stat.js
        fwallet_business: fullReportBusiness,
        fxpath: getShortPath(xPath, 1024),
        fxpath_len: len, // 跟short之后不一样，可以判断出精简过
        fis_event_response: 0, // 比如说，有一个按钮，按了之后，会有下一步的动作，但没有上报，这个值就标志为1，算预埋的点
        fis_embed_event: 0, // 是否有预埋点（预埋点手动上报，所以这里默认为不是预埋点）
      });
    }, 100);
  }
}

function clearFullReport() {
  if (fullReportTimer) {
    clearTimeout(fullReportTimer);
  }
}

/**
 * 移除事件，目前只支持click
 * @param el dom节点
 * @param useCapture 句柄在冒泡还是捕捉阶段执行，默认为true
 */
function removeEvent(el, useCapture) {
  const support = isNode(el) || isDocument(el);
  if (support) {
    el.removeEventListener('click', clickEvent, useCapture);
  }
}

/**
 * 注册事件，目前只支持click
 * @param el 监听节点
 * @param useCapture 句柄在冒泡还是捕捉阶段执行，默认为true
 */
function registerEvent(el= document, useCapture = true) {
  const support = isNode(el) || isDocument(el);
  if (support) {
    el.addEventListener('click', clickEvent, useCapture);
  }
}

function removeFullReport(el = document, useCapture = true, isFullReportOpen = false) {
  /**
   * 是否开启
   */
  if (!isFullReportOpen) {
    return;
  }
  try {
    removeEvent(el, useCapture);
  } catch (e) {
    new CustomException(e, 'FullReportRemoveError', '全量上报移除事件失败');
  }
}

function initFullReport(business,isFullReportOpen = false, el = document, useCapture = true){
  /**
   * 是否开启 isFullReportOpen
   */
  if (!isFullReportOpen) {
    return;
  }
  fullReportBusiness = business;
  try {
    registerEvent(el, useCapture);
  } catch (e) {
    new CustomException(e, 'FullReportInitError', '全量上报初始化事件失败');
  }
}

export {
  clearFullReport,
  removeFullReport,
  initFullReport,
};